/**
 * Generación de OpenAPI 3.1 **desde los contratos Zod** (SSOT S1).
 *
 * El documento se emite en CI (`scripts/emit-openapi.ts`) y el test de paridad
 * verifica que toda ruta declarada en `routes.ts` esté documentada. Si alguien agrega
 * un endpoint y olvida el contrato, el build rompe.
 */
import { z } from 'zod';
import {
  OpenAPIRegistry,
  OpenApiGeneratorV31,
  extendZodWithOpenApi,
} from '@asteasolutions/zod-to-openapi';
import type { OpenAPIObject } from 'openapi3-ts/oas31';

extendZodWithOpenApi(z);

import { ROUTES, routeKey } from './routes.js';
import { ProblemSchema } from './errors.js';
import { CurrencySchema } from './money.js';
import { RulesSnapshotSchema, PoliticaDesbalanceSchema } from './domain/rules.js';
import {
  FightSchema,
  FightMetricsSchema,
  FinalMetricsSchema,
  FighterSnapshotSchema,
} from './domain/fight.js';
import { BetSchema } from './domain/bet.js';
import { PayoutSchema } from './domain/payout.js';
import { PresetSchema, PresetParamsSchema, IdentificacionSchema } from './domain/preset.js';
import { UserSchema, TerminalSchema, OrganizationSchema } from './domain/org.js';
import { AuditEventSchema } from './domain/audit.js';
import { HouseMetricsSchema, ArchivedFightSchema } from './domain/metrics.js';
import { PageQuerySchema } from './domain/common.js';
import * as Req from './api/requests.js';
import * as Res from './api/responses.js';

type AnySchema = z.ZodTypeAny;

interface RouteContract {
  body?: AnySchema;
  query?: AnySchema;
  response: AnySchema;
  tag: string;
}

/** Mapa ruta → contratos de entrada/salida documentados. */
export const CONTRACTS: Record<string, RouteContract> = {
  [routeKey('GET', '/healthz')]: { response: Res.HealthResponse, tag: 'System' },
  [routeKey('GET', '/readyz')]: { response: Res.ReadyResponse, tag: 'System' },
  [routeKey('POST', '/api/v1/auth/otp/request')]: {
    body: Req.RequestOtpBody,
    response: Res.OtpRequestedResponse,
    tag: 'Auth',
  },
  [routeKey('POST', '/api/v1/auth/otp/verify')]: {
    body: Req.VerifyOtpBody,
    response: Res.AuthSessionResponse,
    tag: 'Auth',
  },
  [routeKey('POST', '/api/v1/auth/refresh')]: { response: Res.AuthSessionResponse, tag: 'Auth' },
  [routeKey('GET', '/api/v1/public/fight')]: {
    response: Res.PublicFightResponse,
    tag: 'Stream',
  },
  [routeKey('GET', '/api/v1/public/tickets')]: {
    response: Res.PublicTicketsResponse,
    tag: 'Stream',
  },
  [routeKey('GET', '/api/v1/public/stream')]: {
    response: Res.PublicTicket,
    tag: 'Stream',
  },
  [routeKey('POST', '/api/v1/auth/password')]: {
    body: Req.PasswordLoginBody,
    response: Res.AuthSessionResponse,
    tag: 'Auth',
  },
  [routeKey('POST', '/api/v1/auth/access-code')]: {
    body: Req.AccessCodeLoginBody,
    response: Res.AuthSessionResponse,
    tag: 'Auth',
  },
  [routeKey('POST', '/api/v1/admin/access-codes/rotate')]: {
    response: Res.AccessCodeRotatedResponse,
    tag: 'Auth',
  },
  [routeKey('POST', '/api/v1/auth/logout')]: { response: ProblemSchema, tag: 'Auth' },
  [routeKey('GET', '/api/v1/auth/jwks.json')]: { response: ProblemSchema, tag: 'Auth' },
  [routeKey('GET', '/api/v1/me')]: { response: Res.MeResponse, tag: 'System' },
  [routeKey('PATCH', '/api/v1/me')]: { body: Req.PatchMeBody, response: Res.MeResponse, tag: 'System' },
  [routeKey('GET', '/api/v1/fights')]: { query: PageQuerySchema, response: Res.FightsListResponse, tag: 'Fights' },
  [routeKey('GET', '/api/v1/fights/current')]: { response: Res.CurrentFightResponse, tag: 'Fights' },
  [routeKey('GET', '/api/v1/fights/:id')]: { response: Res.FightResponse, tag: 'Fights' },
  [routeKey('POST', '/api/v1/fights')]: { body: Req.CreateFightBody, response: Res.FightResponse, tag: 'Fights' },
  [routeKey('PATCH', '/api/v1/fights/:id')]: { body: Req.UpdateFightBody, response: Res.FightResponse, tag: 'Fights' },
  [routeKey('POST', '/api/v1/fights/:id/lock')]: { body: Req.VersionedActionBody, response: Res.FightResponse, tag: 'Fights' },
  [routeKey('POST', '/api/v1/fights/:id/settle')]: { body: Req.SettleFightBody, response: Res.SettleResponse, tag: 'Fights' },
  [routeKey('POST', '/api/v1/fights/:id/cancel')]: { body: Req.CancelFightBody, response: Res.FightResponse, tag: 'Fights' },
  [routeKey('GET', '/api/v1/fights/:id/stream')]: { response: Res.FightResponse, tag: 'Fights' },
  [routeKey('GET', '/api/v1/fights/:id/bets')]: { query: PageQuerySchema, response: Res.BetsListResponse, tag: 'Bets' },
  [routeKey('POST', '/api/v1/fights/:id/bets')]: { body: Req.CreateBetBody, response: Res.CreateBetResponse, tag: 'Bets' },
  [routeKey('GET', '/api/v1/bets/mine')]: { query: PageQuerySchema, response: Res.BetsListResponse, tag: 'Bets' },
  [routeKey('GET', '/api/v1/bets/lookup')]: { query: Req.LookupBetQuery, response: Res.BetsListResponse, tag: 'Bets' },
  [routeKey('GET', '/api/v1/bets/:id')]: { response: Res.CreateBetResponse, tag: 'Bets' },
  [routeKey('POST', '/api/v1/bets/:id/pay')]: { body: Req.PayBetBody, response: Res.PayBetResponse, tag: 'Payouts' },
  [routeKey('GET', '/api/v1/payouts/pending')]: { response: Res.PendingPayoutsResponse, tag: 'Payouts' },
  [routeKey('POST', '/api/v1/payouts/settle-all')]: {
    body: Req.SettleAllPendingBody,
    response: Res.SettleAllResponse,
    tag: 'Caja',
  },
  [routeKey('POST', '/api/v1/fights/:id/payouts/settle-all')]: { body: Req.SettleAllBody, response: Res.SettleAllResponse, tag: 'Payouts' },
  [routeKey('GET', '/api/v1/history/fights')]: { query: PageQuerySchema, response: Res.HistoryResponse, tag: 'Fights' },
  [routeKey('GET', '/api/v1/metrics/house')]: { response: Res.HouseMetricsResponse, tag: 'System' },
  [routeKey('GET', '/api/v1/presets')]: { response: Res.PresetsResponse, tag: 'Presets' },
  [routeKey('PUT', '/api/v1/presets/:id')]: { body: Req.UpsertPresetBody, response: Res.PresetResponse, tag: 'Presets' },
  [routeKey('POST', '/api/v1/presets/:id/approve')]: { body: Req.ApprovePresetBody, response: Res.PresetResponse, tag: 'Presets' },
  [routeKey('GET', '/api/v1/users')]: { query: PageQuerySchema, response: Res.UsersListResponse, tag: 'Users' },
  [routeKey('PATCH', '/api/v1/users/:id')]: { body: Req.UpdateUserBody, response: Res.UserResponse, tag: 'Users' },
  [routeKey('GET', '/api/v1/audit')]: { query: PageQuerySchema, response: Res.AuditListResponse, tag: 'Audit' },
  [routeKey('GET', '/api/v1/terminals')]: { response: Res.TerminalsResponse, tag: 'System' },
  [routeKey('POST', '/api/v1/terminals/heartbeat')]: { body: Req.HeartbeatBody, response: Res.TerminalsResponse, tag: 'System' },
  [routeKey('GET', '/api/v1/exports/fivem')]: { response: Res.ExportResponse, tag: 'Exports' },
  [routeKey('GET', '/api/v1/exports/discord')]: { response: Res.ExportResponse, tag: 'Exports' },
  [routeKey('GET', '/api/v1/exports/fight/:id')]: { response: Res.ExportResponse, tag: 'Exports' },
};

/** Rutas sin contrato documentado: el test de paridad exige que esté vacío. */
export function undocumentedRoutes(): string[] {
  return ROUTES.map((r) => routeKey(r.method, r.path)).filter((k) => !CONTRACTS[k]);
}

export function buildOpenApiDocument(version = '1.0.0'): OpenAPIObject {
  const registry = new OpenAPIRegistry();

  registry.register('Problem', ProblemSchema);
  registry.register('Currency', CurrencySchema);
  registry.register('PoliticaDesbalance', PoliticaDesbalanceSchema);
  registry.register('RulesSnapshot', RulesSnapshotSchema);
  registry.register('FighterSnapshot', FighterSnapshotSchema);
  registry.register('Fight', FightSchema);
  registry.register('FightMetrics', FightMetricsSchema);
  registry.register('FinalMetrics', FinalMetricsSchema);
  registry.register('Bet', BetSchema);
  registry.register('Payout', PayoutSchema);
  registry.register('Preset', PresetSchema);
  registry.register('PresetParams', PresetParamsSchema);
  registry.register('Identificacion', IdentificacionSchema);
  registry.register('User', UserSchema);
  registry.register('Terminal', TerminalSchema);
  registry.register('Organization', OrganizationSchema);
  registry.register('AuditEvent', AuditEventSchema);
  registry.register('HouseMetrics', HouseMetricsSchema);
  registry.register('ArchivedFight', ArchivedFightSchema);

  for (const route of ROUTES) {
    const contract = CONTRACTS[routeKey(route.method, route.path)];
    const openApiPath = route.path.replace(/:(\w+)/g, '{$1}');
    const secured = route.auth.kind !== 'public';

    // El tipo `request` de la librería es una unión que TypeScript no puede estrechar
    // a partir de un spread condicional; se construye explícitamente.
    const request: {
      body?: { content: Record<string, { schema: AnySchema }> };
      query?: AnySchema;
    } = {};
    if (contract?.body) request.body = { content: { 'application/json': { schema: contract.body } } };
    if (contract?.query) request.query = contract.query;

    registry.registerPath({
      method: route.method.toLowerCase() as 'get' | 'post' | 'patch' | 'put' | 'delete',
      path: openApiPath,
      summary: route.summary,
      tags: contract ? [contract.tag] : ['System'],
      security: secured ? [{ bearerAuth: [] }] : [],
      request: request as unknown as Parameters<typeof registry.registerPath>[0]['request'],
      responses: {
        200: {
          description: 'OK',
          content: {
            'application/json': { schema: contract ? contract.response : z.object({}) },
          },
        },
        default: {
          description: 'Error (RFC 9457 Problem+json)',
          content: { 'application/problem+json': { schema: ProblemSchema } },
        },
      },
    });
  }

  registry.registerComponent('securitySchemes', 'bearerAuth', {
    type: 'http',
    scheme: 'bearer',
    bearerFormat: 'JWT',
  });

  const generator = new OpenApiGeneratorV31(registry.definitions);
  return generator.generateDocument({
    openapi: '3.1.0',
    info: {
      title: 'THE PIT · LOS SANTOS API',
      version,
      description:
        'API de apuestas pari-mutuel multi-terminal. Contratos generados desde Zod (SSOT). ' +
        'Errores en RFC 9457. Mutaciones críticas requieren Idempotency-Key.',
    },
    servers: [
      { url: 'https://thepit-api.fly.dev', description: 'producción' },
      { url: 'http://localhost:8080', description: 'local' },
    ],
    tags: [
      { name: 'Auth' },
      { name: 'Fights' },
      { name: 'Bets' },
      { name: 'Payouts' },
      { name: 'Presets' },
      { name: 'Users' },
      { name: 'Audit' },
      { name: 'Exports' },
      { name: 'System' },
    ],
  });
}
